import React, { useEffect, useRef } from "react";

/**
 * Free, no-API-key map showing precisely labelled pins for our two showrooms.
 * Uses Leaflet via CDN + OpenStreetMap tiles.
 *
 * Props:
 *   - stores: [{ label: string, lat: number, lng: number, sub?: string }]
 */
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

const ensureLeaflet = () => {
  if (window.L) return Promise.resolve(window.L);
  return new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      existing.addEventListener("error", reject);
      if (window.L) resolve(window.L);
      return;
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

const StoreMap = ({ stores }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    ensureLeaflet().then((L) => {
      if (cancelled || !containerRef.current || mapRef.current) return;

      const center = [
        (stores[0].lat + stores[1].lat) / 2,
        (stores[0].lng + stores[1].lng) / 2,
      ];

      const map = L.map(containerRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true,
      }).setView(center, 19);

      // Clean light tile set — Carto Voyager (free, no API key, vector-ish style)
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 20,
        }
      ).addTo(map);

      stores.forEach((s) => {
        const icon = L.divIcon({
          className: "style-india-pin",
          html: `
            <div class="sip-wrapper">
              <div class="sip-label">${s.label}</div>
              <div class="sip-dot"></div>
              <div class="sip-tip"></div>
            </div>
          `,
          iconSize: [160, 64],
          iconAnchor: [80, 64],
          popupAnchor: [0, -60],
        });

        L.marker([s.lat, s.lng], { icon })
          .addTo(map)
          .bindPopup(
            `<div style="font-family:'Cormorant Garamond',serif;font-size:16px;color:#0F1245;font-weight:600;">${s.label}</div>${
              s.sub
                ? `<div style="font-family:Outfit,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#4A3FCF;margin-top:4px;">${s.sub}</div>`
                : ""
            }`
          );
      });

      // Dashed connector line between the two shops
      if (stores.length >= 2) {
        L.polyline(
          stores.slice(0, 2).map((s) => [s.lat, s.lng]),
          {
            color: "#4A3FCF",
            weight: 2,
            opacity: 0.65,
            dashArray: "4 6",
            interactive: false,
          }
        ).addTo(map);
      }

      // "Walk between shops · 1 min" badge (Leaflet custom control)
      const WalkBadge = L.Control.extend({
        options: { position: "bottomleft" },
        onAdd: function () {
          const div = L.DomUtil.create("div", "style-india-walk-badge");
          div.innerHTML = `
            <span class="sib-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="13" cy="4" r="2"></circle>
                <path d="M15 22l-2-7-4 3-2-4 5-5 3 2 4 1"></path>
              </svg>
            </span>
            <span class="sib-text">Walk between shops · <strong>1 min</strong></span>
          `;
          L.DomEvent.disableClickPropagation(div);
          return div;
        },
      });
      new WalkBadge().addTo(map);

      // Fit bounds with a little padding so both pins are visible & labels don't clip
      const bounds = L.latLngBounds(stores.map((s) => [s.lat, s.lng]));
      map.fitBounds(bounds, { padding: [70, 70], maxZoom: 19 });

      mapRef.current = map;
    });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [stores]);

  return (
    <div
      ref={containerRef}
      data-testid="showroom-map"
      className="w-full h-full"
      style={{ background: "#F4F7FE" }}
    />
  );
};

export default StoreMap;
